import { useState } from 'react'
import { ChevronDownIcon, DownloadIcon } from 'lucide-react'
import {
	AlertBanner,
	cn,
	IconButton,
	Spinner,
	Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@rentflowdev/ui'
import { ACTIVE_BUILD_JOB_STATUSES, useListBuildJobsQuery } from '@/api'
import type { BuildJob, BuildJobStatus } from '@rentflowdev/api'
import { DEFAULT_LOCALE } from '@/types'
import { extractErrorMessage } from '@/lib/error-message'
import { BuildJobStatusBadge } from './build-job-status-badge'

const JOBS_PAGE_SIZE = 50

function isActiveStatus(status: BuildJobStatus): boolean {
	return (ACTIVE_BUILD_JOB_STATUSES as readonly BuildJobStatus[]).includes(status)
}

function formatDateTime(iso: string): string {
	return new Date(iso).toLocaleString(DEFAULT_LOCALE, {
		day: '2-digit', month: '2-digit', year: 'numeric',
		hour: '2-digit', minute: '2-digit',
	})
}

function formatDuration(startedAt: string | null, finishedAt: string | null): string {
	if (!startedAt || !finishedAt) return '—'
	const ms = new Date(finishedAt).getTime() - new Date(startedAt).getTime()
	if (!Number.isFinite(ms) || ms < 0) return '—'
	const totalSec = Math.round(ms / 1000)
	const min = Math.floor(totalSec / 60)
	const sec = totalSec % 60
	return min > 0 ? `${min} мин ${sec} с` : `${sec} с`
}

interface BuildJobsTableProps {
	profileId: string
}

/** Build history of a profile. Polls every 5s while any job is in progress. */
export function BuildJobsTable({ profileId }: BuildJobsTableProps) {
	const [expandedJobId, setExpandedJobId] = useState<string | null>(null)

	const { data, isLoading, error } = useListBuildJobsQuery({
		profileId,
		limit: JOBS_PAGE_SIZE,
	})

	const jobs = data?.jobs ?? []
	const hasActiveJob = jobs.some((job) => isActiveStatus(job.status))

	// Second subscription to the same cache entry — it exists only while a job
	// is in progress and contributes a 5s polling interval (RTK Query applies
	// the smallest interval among subscribers). Once every job reaches a
	// terminal status the subscription is skipped and polling stops.
	useListBuildJobsQuery(
		{ profileId, limit: JOBS_PAGE_SIZE },
		{ pollingInterval: 5000, skip: !hasActiveJob },
	)

	if (isLoading) {
		return (
			<div className="flex items-center justify-center py-10">
				<Spinner />
			</div>
		)
	}

	if (error) {
		return (
			<AlertBanner
				variant="destructive"
				title="Не удалось загрузить историю сборок"
				description={extractErrorMessage(error)}
			/>
		)
	}

	if (jobs.length === 0) {
		return (
			<p className="py-6 text-center text-sm text-(--color-muted-foreground)">
				Сборок ещё не было — нажмите «Собрать приложение», чтобы запустить первую.
			</p>
		)
	}

	return (
		<div className="rounded-lg border border-(--color-border) overflow-hidden">
			<Table>
				<TableHeader>
					<TableRow className="hover:bg-transparent">
						<TableHead className="text-(--color-muted-foreground) font-[500]">Статус</TableHead>
						<TableHead className="text-(--color-muted-foreground) font-[500]">Версия</TableHead>
						<TableHead className="text-(--color-muted-foreground) font-[500]">Запросил</TableHead>
						<TableHead className="text-(--color-muted-foreground) font-[500]">Создана</TableHead>
						<TableHead className="text-(--color-muted-foreground) font-[500]">Длительность</TableHead>
						<TableHead className="text-(--color-muted-foreground) font-[500]">Артефакт</TableHead>
						<TableHead className="text-(--color-muted-foreground) font-[500]">RuStore</TableHead>
						<TableHead className="w-10" />
					</TableRow>
				</TableHeader>
				<TableBody>
					{jobs.map((job: BuildJob) => {
						const isExpanded = expandedJobId === job.id
						return (
							<TableRowGroup
								key={job.id}
								job={job}
								isExpanded={isExpanded}
								onToggle={() =>
									setExpandedJobId((prev) => (prev === job.id ? null : job.id))
								}
							/>
						)
					})}
				</TableBody>
			</Table>
		</div>
	)
}

interface TableRowGroupProps {
	job: BuildJob
	isExpanded: boolean
	onToggle: () => void
}

function TableRowGroup({ job, isExpanded, onToggle }: TableRowGroupProps) {
	return (
		<>
			<TableRow className="h-14">
				<TableCell className="px-4">
					<BuildJobStatusBadge status={job.status} />
				</TableCell>
				<TableCell className="px-4 text-sm text-(--color-foreground)">
					{job.versionName} ({job.versionCode})
				</TableCell>
				<TableCell className="px-4">
					{job.requestedBy ? (
						<span
							className="font-mono text-xs text-(--color-muted-foreground)"
							title={job.requestedBy}
						>
							{job.requestedBy.slice(0, 8)}…
						</span>
					) : (
						<span className="text-sm text-(--color-muted-foreground)">—</span>
					)}
				</TableCell>
				<TableCell className="px-4 text-sm text-(--color-muted-foreground)">
					{formatDateTime(job.createdAt)}
				</TableCell>
				<TableCell className="px-4 text-sm text-(--color-muted-foreground)">
					{formatDuration(job.startedAt, job.finishedAt)}
				</TableCell>
				<TableCell className="px-4">
					{job.artifactUrl ? (
						<a
							href={job.artifactUrl}
							target="_blank"
							rel="noreferrer"
							className="inline-flex items-center gap-1 text-sm text-(--color-info) underline-offset-2 hover:underline"
						>
							<DownloadIcon className="size-3.5" />
							Скачать
						</a>
					) : (
						<span className="text-sm text-(--color-muted-foreground)">—</span>
					)}
				</TableCell>
				<TableCell className="px-4 text-sm text-(--color-muted-foreground)">
					{job.rustoreVersionId ?? '—'}
				</TableCell>
				<TableCell className="px-2 text-right">
					{job.status === 'FAILED' && job.error && (
						<IconButton
							variant="ghost"
							size="sm"
							aria-label={isExpanded ? 'Скрыть ошибку' : 'Показать ошибку'}
							onClick={onToggle}
						>
							<ChevronDownIcon
								className={cn('size-4 transition-transform', isExpanded && 'rotate-180')}
							/>
						</IconButton>
					)}
				</TableCell>
			</TableRow>
			{isExpanded && job.error && (
				<TableRow className="hover:bg-transparent">
					<TableCell colSpan={8} className="bg-(--color-destructive)/5 px-4 py-3">
						<pre className="whitespace-pre-wrap break-words font-mono text-xs text-(--color-destructive)">
							{job.error}
						</pre>
					</TableCell>
				</TableRow>
			)}
		</>
	)
}
